import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Card,
  CardContent,
  CardMedia
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useAuth } from '../../contexts/AuthContext';
import { createArticle } from '../../services/articleService';
import { validateArticleUrl, extractArticleMetadata } from '../../utils/articleValidation';
import { CAUSES } from '../layout/causes';
import { ArticleMetadata } from '../../utils/articleValidation';

interface ArticleSubmitFormProps {
  onSubmitSuccess?: () => void;
}

export default function ArticleSubmitForm({ onSubmitSuccess }: ArticleSubmitFormProps) {
  const { currentUser } = useAuth();
  const [articleUrl, setArticleUrl] = useState('');
  const [selectedCauses, setSelectedCauses] = useState<string[]>([]);
  const [primaryCause, setPrimaryCause] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [metadata, setMetadata] = useState<ArticleMetadata | null>(null);
  const [validating, setValidating] = useState(false);

  const handleUrlChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setArticleUrl(url);
    setError(null);
    setMetadata(null);

    if (url.trim()) {
      setValidating(true);
      try {
        const validation = validateArticleUrl(url);
        if (!validation.isValid) {
          setError(validation.error || 'Invalid article URL');
          return;
        }

        // Extract metadata
        const extractedMetadata = await extractArticleMetadata(url);
        setMetadata(extractedMetadata);
        
        // Show a warning if metadata extraction failed (fallback was used)
        if (extractedMetadata.title === 'Article from ' + extractedMetadata.source) {
          setError('Warning: Could not extract article details automatically. You can still share the article, but some information may be missing.');
        }
      } catch (err) {
        setError('Failed to validate article URL. Please try again.');
      } finally {
        setValidating(false);
      }
    }
  };

  const handleCauseChange = (e: SelectChangeEvent<string>) => {
    const cause = e.target.value;
    if (cause && !selectedCauses.includes(cause)) {
      if (selectedCauses.length >= 3) {
        setError('You can only select a maximum of 3 causes.');
        return;
      }
      setSelectedCauses(prev => [...prev, cause]);
      if (!primaryCause) {
        setPrimaryCause(cause);
      }
    }
  };

  const handlePrimaryCauseChange = (e: SelectChangeEvent<string>) => {
    setPrimaryCause(e.target.value);
  };

  const handleRemoveCause = (causeToRemove: string) => {
    setSelectedCauses(prev => prev.filter(cause => cause !== causeToRemove));
    if (primaryCause === causeToRemove) {
      setPrimaryCause(selectedCauses.find(cause => cause !== causeToRemove) || '');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('You must be signed in to share articles.');
      return;
    }

    if (!articleUrl.trim()) {
      setError('Please enter an article URL.');
      return;
    }

    if (!primaryCause) {
      setError('Please select a primary cause.');
      return;
    }

    if (selectedCauses.length === 0) {
      setError('Please select at least one cause.');
      return;
    }

    if (selectedCauses.length > 3) {
      setError('You can only select a maximum of 3 causes.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await createArticle(articleUrl, selectedCauses, primaryCause, currentUser, metadata || undefined);
      setSuccess(true);
      setArticleUrl('');
      setSelectedCauses([]);
      setPrimaryCause('');
      setMetadata(null);
      
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to share article. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          Article shared successfully!
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => setSuccess(false)}
        >
          Share Another Article
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Share articles from reliable news sources. We accept articles from: BBC, The Guardian, Reuters, Al Jazeera, CNN, New York Times, Washington Post, Associated Press, NPR, Financial Times, ABC Australia, and The Telegraph.
      </Typography>

      <Stack spacing={3}>
        <TextField
          fullWidth
          label="Article URL"
          placeholder="https://www.bbc.com/news/..."
          value={articleUrl}
          onChange={handleUrlChange}
          disabled={loading}
          error={!!error && !validating}
          helperText={error}
          sx={{ mb: 2 }}
        />

        {validating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Validating article...
            </Typography>
          </Box>
        )}

        {metadata && (
          <Card sx={{ mb: 2 }}>
            <CardMedia
              component="img"
              height="140"
              image={metadata.thumbnail}
              alt={metadata.title}
              sx={{ objectFit: 'cover' }}
            />
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {metadata.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {metadata.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Source: {metadata.source}
                {metadata.author && ` • Author: ${metadata.author}`}
              </Typography>
            </CardContent>
          </Card>
        )}

        <FormControl fullWidth>
          <InputLabel>Add Causes</InputLabel>
          <Select
            value=""
            label="Add Causes"
            onChange={handleCauseChange}
            disabled={loading || selectedCauses.length >= 3}
          >
            {CAUSES.map((cause) => (
              <MenuItem key={cause.id} value={cause.id}>
                {cause.name}
              </MenuItem>
            ))}
          </Select>
          {selectedCauses.length >= 3 && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              Maximum of 3 causes reached
            </Typography>
          )}
        </FormControl>

        {selectedCauses.length > 0 && (
          <Box>
            <Typography variant="body2" gutterBottom>
              Selected Causes:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {selectedCauses.map((cause) => (
                <Chip
                  key={cause}
                  label={CAUSES.find(c => c.id === cause)?.name || cause}
                  onDelete={() => handleRemoveCause(cause)}
                  color={primaryCause === cause ? 'primary' : 'default'}
                  variant={primaryCause === cause ? 'filled' : 'outlined'}
                  onClick={() => setPrimaryCause(cause)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {selectedCauses.length > 0 && (
          <FormControl fullWidth>
            <InputLabel>Primary Cause</InputLabel>
            <Select
              value={primaryCause}
              label="Primary Cause"
              onChange={handlePrimaryCauseChange}
              disabled={loading}
            >
              {selectedCauses.map((cause) => (
                <MenuItem key={cause} value={cause}>
                  {CAUSES.find(c => c.id === cause)?.name || cause}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading || !articleUrl.trim() || !primaryCause || selectedCauses.length === 0}
          sx={{ mt: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Share Article'}
        </Button>
      </Stack>
    </Box>
  );
} 